import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LocationFilter from '../locaties/LocationFilter';
import formatDate from '../../utilities';

function ListAllNotes() {
  const [notes, setNotes] = useState([]);
  const [zoekterm, setZoekterm] = useState('');
  const [debouncedZoekterm, setDebouncedZoekterm] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  const { userInfo } = useAuth();
  const token = sessionStorage.getItem('jwtToken');
  const userRole = userInfo?.role || '';
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [zoekterm]);

  const fetchNotes = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/notes/listall`,
        {
          params: {
            page,
            limit,
            search: debouncedZoekterm,
            location_id: locationFilter
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setNotes(Array.isArray(res.data?.data) ? res.data.data : []);
      setPagination(
        res.data?.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        }
      );
    } catch (err) {
      console.error('Fout bij ophalen notities:', err);
      setNotes([]);
      setPagination({
        page: 1,
        limit,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, debouncedZoekterm, locationFilter]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'docent') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => {
          console.error('Fout bij ophalen locaties:', err);
          setLocations([]);
        });
    } else {
      setLocations([]);
    }
  }, [userRole, token]);

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze notitie wilt verwijderen?')) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/notes/delete/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchNotes();
    } catch (error) {
      console.error('Fout bij verwijderen notitie:', error);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    }

    if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    const buttons = [];

    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="container-fluid px-3 px-md-5 mt-4">
      <div className="row mb-4 align-items-center">
        <div className="col-12 col-md-6 text-center text-md-start mb-2 mb-md-0">
          <h2 className="h4">Alle Notities</h2>
        </div>
        <div className="col-12 col-md-6 text-md-end text-center">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/notes/create')}
          >
            <i className="bi bi-plus-circle me-2"></i>Nieuw
          </button>
        </div>
      </div>

      {(userRole === 'super_admin' || userRole === 'docent') && (
        <div className="mb-3">
          <LocationFilter
            userRole={userRole}
            locationFilter={locationFilter}
            setLocationFilter={(value) => {
              setLocationFilter(value);
              setPage(1);
            }}
            locations={locations}
          />
        </div>
      )}

      <div className="row mb-3">
        <div className={`col-12 ${(userRole === 'super_admin' || userRole === 'docent') ? 'col-md-6' : 'col-md-8'} mb-2`}>
          <input
            type="text"
            className="form-control rounded-0"
            placeholder="Zoek op voornaam of achternaam"
            value={zoekterm}
            onChange={(e) => {
              setZoekterm(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="col-12 col-md-2 mb-2">
          <select
            className="form-select rounded-0"
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={10}>Toon 10</option>
            <option value={20}>Toon 20</option>
            <option value={50}>Toon 50</option>
            <option value={100}>Toon 100</option>
          </select>
        </div>

        <div className={`col-12 ${(userRole === 'super_admin' || userRole === 'docent') ? 'col-md-4' : 'col-md-2'} text-md-end`}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setZoekterm('');
              setLocationFilter('all');
              setLimit(20);
              setPage(1);
            }}
          >
            Filters wissen
          </button>
        </div>
      </div>

      <div className="row mb-3 align-items-center">
        <div className="col-12">
          <small className="text-muted">
            Toont {startRecord} t/m {endRecord} van {pagination.total || 0} notities
          </small>
        </div>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="row">
            {notes.map((note) => {
              const preview =
                note.content.length > 50
                  ? `${note.content.slice(0, 50)}...`
                  : note.content;

              return (
                <div key={note.id} className="col-12 col-md-6 col-lg-4 mb-4">
                  <div
                    className="card h-100 shadow-sm cursor-pointer"
                    onClick={() => navigate(`/notes/view/${note.id}`)}
                  >
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">{note.studentName}</h5>
                      <h6 className="card-subtitle mb-2 text-muted">
                        {formatDate(note.noteDate)}
                      </h6>
                      <p className="card-text flex-grow-1">{preview}</p>
                      <p className="card-text text-muted">
                        <small>Aangemaakt door: {note.createdBy}</small>
                      </p>

                      {userRole !== 'docent' && (
                        <div
                          className="mt-1 d-flex justify-content-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            to={`/notes/update/${note.id}`}
                            className="btn btn-sm btn-warning px-2 py-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Bewerk
                          </Link>
                          <button
                            onClick={() => handleDelete(note.id)}
                            className="btn btn-sm btn-danger px-2 py-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Verwijder
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {notes.length === 0 && (
              <div className="col-12 text-center text-muted">
                Geen notities gevonden.
              </div>
            )}
          </div>

          {(pagination.totalPages || 1) > 1 && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                  Vorige
                </button>

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={!pagination.hasNextPage}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Volgende
                </button>
              </div>

              <div className="d-flex flex-wrap justify-content-center gap-2">
                {renderPaginationButtons()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ListAllNotes;